import { NewOrderScenario, TNewOrderForm } from "@/entities/new-order";
import { Checkbox } from "@/shared/ui/checkbox";
import Hint from "@/shared/ui/hint";
import modalStyles from "@/Theme/modalsElements.module.scss";
import { getDataFromStorage } from "@/Utils/localStorageHelpers";
import { Flex } from "@mantine/core";
import { getTimeRange, TimePicker } from "@mantine/dates";
import { Modal } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styles from "./attributes-block.module.scss";
import { DelayAttentionModal } from "./delay-attention-modal";

const toMinutes = (value: string) => {
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
};

const fromMinutes = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const DelayStartAttribute = () => {
  const { setValue, watch } = useFormContext<TNewOrderForm>();

  const { delayedStart, activeScenario, dateStart, countriesList } = watch();

  const [attentionIsViewed, setAttentionIsViewed] = useState(false);
  const [openDelayAttentionModal, setOpenDelayAttentionModal] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  // TODO очистка при смене даты
  const minDate = useMemo(() => {
    const now = dayjs();

    if (dateStart && dayjs(dateStart).utc().isBefore(now)) {
      return now.utc().startOf("h").add(1, "h").format("HH:mm");
    }

    return "";
  }, [dateStart]);

  const isDisabledDelay = useMemo(() => {
    return (
      !delayedStart.enable ||
      !dateStart ||
      activeScenario === NewOrderScenario.extend
    );
  }, [dateStart, delayedStart.enable, activeScenario]);

  const checkingCountriesCount = useCallback(
    (checked: boolean) => {
      // Проверяет количество стран и статус отключения показа модалки предупреждения
      const disableModal = getDataFromStorage("disable_delay_modal");

      if (
        countriesList.length > 1 &&
        !disableModal &&
        checked &&
        !attentionIsViewed
      ) {
        setOpenDelayAttentionModal(true);
      }
    },
    [attentionIsViewed, countriesList],
  );

  useEffect(() => {
    checkingCountriesCount(delayedStart.enable);
  }, [countriesList, checkingCountriesCount, delayedStart.enable]);

  const handleCloseModal = useCallback(() => {
    setOpenDelayAttentionModal(false);
    setAttentionIsViewed(true);
  }, []);

  const handleToggleDelay = (event: React.ChangeEvent<HTMLInputElement>) => {
    checkingCountriesCount(event.target.checked);
    setValue("delayedStart.enable", event.target.checked);
  };

  const handleChangeTime = (value: string | null) => {
    setInputValue(value ?? "");

    if (!value) {
      setValue("delayedStart.value", null);
      return;
    }

    setValue("delayedStart.value", value);
  };

  useEffect(() => {
    setInputValue(delayedStart.value ?? "");
  }, [delayedStart.value]);

  const applyClamp = (value: string) => {
    const min = minDate ? toMinutes(minDate.slice(0, 5)) : 0;
    const max = 23 * 60 + 59;

    let current = toMinutes(value);

    current = Math.max(min, Math.min(current, max));

    const normalized = fromMinutes(current);

    setInputValue(normalized);
    setValue("delayedStart.value", normalized);
  };

  return (
    <Flex className={styles.row} align="center">
      <div className={styles.row_heading}>
        <div className={styles.heading}>
          <span className={styles.title}>Delayed start</span>
          <Hint popupText="Specify the exact time when you need to start the campaign." />
        </div>
        <Checkbox
          name="attributes"
          checked={delayedStart.enable}
          onChange={handleToggleDelay}
          isDisabled={activeScenario === NewOrderScenario.extend}
        />
      </div>
      <span data-is-disabled={isDisabledDelay} className={styles.title}>
        Set time
      </span>
      <div>
        <div className={styles.delay_body}>
          <div className={styles.delay_selector}>
            <TimePicker
              disabled={isDisabledDelay}
              w={100}
              value={inputValue}
              onChange={handleChangeTime}
              onBlur={() => applyClamp(inputValue)}
              placeholder="Select offset"
              withDropdown
              presets={getTimeRange({
                startTime: minDate,
                endTime: "23:59",
                interval: "00:30",
              })}
              size="sm"
              error={
                delayedStart.enable &&
                !dateStart && (
                  <span className={styles.delay_warning}>
                    Specify the start date of the campaign
                  </span>
                )
              }
            />
          </div>
        </div>
      </div>
      <span className={styles.title} data-is-disabled={isDisabledDelay}>
        UTC +0
      </span>
      <Modal
        open={openDelayAttentionModal}
        onClose={handleCloseModal}
        className={modalStyles.modal}
      >
        <DelayAttentionModal onClose={handleCloseModal} />
      </Modal>
    </Flex>
  );
};
